import * as THREE from 'three';
import { STARS } from '../core/SimConfig.js';
import { DEG2RAD } from '../utils.js';
import starVert from '../../glsl/star_vert.glsl';
import starFrag from '../../glsl/star_frag.glsl';

/**
 * Procedural star field from the Yale Bright Star Catalog.
 *
 * The night scenario's background plate has its stars baked in at 2K, which is
 * roughly 6 arcminutes per pixel — an order of magnitude coarser than the eye,
 * so they smear into blurry blobs the moment you look up through the canopy.
 * This draws the real thing instead: 9,096 catalogue stars as point sprites,
 * placed by right ascension and declination, sized and coloured from their
 * actual visual magnitude and B−V colour index. `night_sky_starless_2k.jpg`
 * is the plate underneath — the source JPG with its baked stars medianed out,
 * keeping the moon, its halo and the Milky Way nebulosity.
 *
 * Data is `public/assets/data/stars.bin`, packed by tools/build-stars.mjs —
 * 6 bytes per star, sorted brightest-first so a magnitude cutoff is a draw-range
 * prefix rather than a buffer rebuild.
 *
 * The object lives in the main scene, parked at the camera every frame so the
 * sky stays unreachable, and carries the celestial orientation in its own
 * rotation. It draws with depth test on but no depth write, before the other
 * transparent objects, so terrain occludes it and nothing occludes the sky.
 *
 * Persists across scenarios like CloudLayer; `configure()` re-reads the active
 * lighting condition and self-disables where there is no `stars` block.
 */
export default class StarField {
  /**
   * @param {{ camera: THREE.Camera, data: ArrayBuffer }} opts
   */
  constructor({ camera, data }) {
    this.camera = camera;
    this._time = 0;
    this._mags = null;   // decoded magnitudes, ascending — for the cutoff search

    this.uniforms = {
      uRadius:          { value: STARS.radius },
      uPixelRatio:      { value: 1 },
      uTime:            { value: 0 },
      uMagRef:          { value: STARS.magnitudeRef },
      uSizeMin:         { value: STARS.sizeMin },
      uSizeMax:         { value: STARS.sizeMax },
      uSizeScale:       { value: STARS.sizeScale },
      uSizeGamma:       { value: STARS.sizeGamma },
      uBrightness:      { value: STARS.brightness },
      uBrightnessGamma: { value: STARS.brightnessGamma },
      uTwinkle:         { value: STARS.twinkle },
      uTwinkleSpeed:    { value: STARS.twinkleSpeed },
      uTwinkleHorizon:  { value: STARS.twinkleHorizon },
      uFadeLow:         { value: Math.sin(STARS.fadeLowDeg * DEG2RAD) },
      uFadeHigh:        { value: Math.sin(STARS.fadeHighDeg * DEG2RAD) },
      uOpacity:         { value: 1 },
      uCoreSharpness:   { value: STARS.coreSharpness },
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: starVert,
      fragmentShader: starFrag,
      // transparent:true is required for the renderer to honour `blending` at
      // all (it forces NoBlending otherwise), which puts the field in the
      // transparent queue — hence the negative renderOrder, so smoke and trails
      // still composite over the sky rather than under it. Depth is tested but
      // never written, so terrain occludes stars and stars occlude nothing.
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
      fog: false,
    });

    this.points = new THREE.Points(this.#buildGeometry(data), this.material);
    this.points.renderOrder = -1;
    this.points.frustumCulled = false; // the sky is always in view
    this.points.matrixAutoUpdate = true;
    this.points.visible = false;       // enabled by configure()

    this.setOrientation(STARS.latitude, STARS.siderealTime, STARS.northAzimuth);
    this.setMagnitudeLimit(STARS.magnitudeLimit);
  }

  /** The Object3D to add to the scene. */
  get object() { return this.points; }

  // ── Data ──────────────────────────────────────────────────────────────────

  /**
   * Decode stars.bin into geometry. See tools/build-stars.mjs for the layout;
   * angles are uint16 (~20 arcsec, well under a pixel), magnitude and colour
   * index are uint8.
   */
  #buildGeometry(data) {
    const header = new DataView(data);
    const magic = String.fromCharCode(...new Uint8Array(data, 0, 4));
    if (magic !== 'BSC1') throw new Error(`stars.bin: bad magic "${magic}"`);
    const n = header.getUint32(4, true);

    const raRaw = new Uint16Array(data, 8, n);
    const decRaw = new Uint16Array(data, 8 + n * 2, n);
    const magRaw = new Uint8Array(data, 8 + n * 4, n);
    const bvRaw = new Uint8Array(data, 8 + n * 5, n);

    const colorLUT = buildColorLUT(STARS.saturation);

    const positions = new Float32Array(n * 3);
    const mags = new Float32Array(n);
    const colors = new Float32Array(n * 3);

    for (let i = 0; i < n; i++) {
      const ra = raRaw[i] / 65536 * Math.PI * 2;
      const dec = decRaw[i] / 65536 * Math.PI - Math.PI / 2;

      // Equatorial Cartesian: +x → RA 0h on the celestial equator, +z → the
      // north celestial pole. setOrientation() rotates this into world space.
      const cosDec = Math.cos(dec);
      positions[i * 3]     = cosDec * Math.cos(ra);
      positions[i * 3 + 1] = cosDec * Math.sin(ra);
      positions[i * 3 + 2] = Math.sin(dec);

      mags[i] = magRaw[i] / 16 - 2;

      const c = bvRaw[i] * 3;
      colors[i * 3]     = colorLUT[c];
      colors[i * 3 + 1] = colorLUT[c + 1];
      colors[i * 3 + 2] = colorLUT[c + 2];
    }

    this._mags = mags;

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('aMag', new THREE.BufferAttribute(mags, 1));
    geo.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    // frustumCulled is off, but a bounding sphere still keeps raycasts and any
    // future culling honest about the object's extent.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1);
    return geo;
  }

  // ── Orientation ───────────────────────────────────────────────────────────

  /**
   * Aim the celestial sphere for an observer.
   *
   * @param {number} latitude      degrees north
   * @param {number} siderealTime  local sidereal time in hours — the right
   *                               ascension currently crossing the meridian,
   *                               which is what actually picks the visible sky
   * @param {number} northAzimuth  degrees to spin the whole sky about the
   *                               vertical, for aligning it to taste
   *
   * Builds the equatorial → world rotation directly rather than transforming
   * star by star. With hour angle H = LST − α and latitude φ, the standard
   * horizontal conversion is
   *
   *   up    =  cos φ·cos H·cos δ + sin φ·sin δ
   *   north = −sin φ·cos H·cos δ + cos φ·sin δ
   *   east  = −sin H·cos δ
   *
   * which factors into a single rotation once north/east/up are mapped onto the
   * world axes. Note the two sign flips it contains: hour angle runs westward
   * while right ascension runs eastward, and the horizon frame is handed the
   * other way from the equatorial one — they cancel, leaving a proper rotation.
   *
   * World axes here are the true geographic ones for a Y-up right-handed scene:
   * north = −Z, east = +X, up = +Y. That keeps constellations un-mirrored, which
   * is the whole point of using real positions. It does mean the sky rises over
   * +X while the sim's own heading convention (forward = −sin h, −cos h, so
   * heading 090 points at −X) calls that west; `northAzimuth` is there if you
   * would rather line the sky up with the in-game compass than with reality.
   */
  setOrientation(latitude, siderealTime, northAzimuth = 0) {
    this.latitude = latitude;
    this.siderealTime = siderealTime;
    this.northAzimuth = northAzimuth;

    const phi = latitude * DEG2RAD;
    const theta = siderealTime * 15 * DEG2RAD; // hours → radians
    const sp = Math.sin(phi), cp = Math.cos(phi);
    const st = Math.sin(theta), ct = Math.cos(theta);

    // Rows are the world x/y/z axes expressed in equatorial coordinates.
    const m = new THREE.Matrix4().set(
      -st,      ct,      0,   0,   // east   → +X
      cp * ct,  cp * st, sp,  0,   // up     → +Y
      sp * ct,  sp * st, -cp, 0,   // south  → +Z
      0,        0,       0,   1,
    );

    this.points.quaternion.setFromRotationMatrix(m);
    if (northAzimuth) {
      this.points.quaternion.premultiply(
        new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), northAzimuth * DEG2RAD),
      );
    }
  }

  /**
   * Drop everything fainter than `limit`. The catalogue is sorted brightest
   * first, so this is a draw-range prefix — free to change at runtime.
   */
  setMagnitudeLimit(limit) {
    this.magnitudeLimit = limit;
    const mags = this._mags;
    // Binary search for the first star past the cutoff.
    let lo = 0, hi = mags.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (mags[mid] <= limit) lo = mid + 1; else hi = mid;
    }
    this.points.geometry.setDrawRange(0, lo);
    this.visibleCount = lo;
  }

  // ── Per-condition setup ───────────────────────────────────────────────────

  /**
   * Apply a lighting condition. `cfg.stars` is the per-condition block (see
   * LIGHTING.night); a condition without one leaves the field switched off,
   * which is how every daylight scenario opts out.
   */
  configure(cfg) {
    const s = cfg?.stars;
    this.points.visible = !!s && s.enabled !== false;
    if (!this.points.visible) return;

    const u = this.uniforms;
    u.uOpacity.value = s.brightness ?? 1;
    u.uMagRef.value = s.magnitudeRef ?? STARS.magnitudeRef;
    u.uTwinkle.value = s.twinkle ?? STARS.twinkle;

    if (s.magnitudeLimit != null && s.magnitudeLimit !== this.magnitudeLimit) {
      this.setMagnitudeLimit(s.magnitudeLimit);
    }
    if (s.latitude != null || s.siderealTime != null || s.northAzimuth != null) {
      this.setOrientation(
        s.latitude ?? this.latitude,
        s.siderealTime ?? this.siderealTime,
        s.northAzimuth ?? this.northAzimuth,
      );
    }
  }

  /** Point sprites size in physical pixels, so DPR has to be tracked. */
  setPixelRatio(ratio) {
    this.uniforms.uPixelRatio.value = ratio;
  }

  // ── Frame ─────────────────────────────────────────────────────────────────

  update(dt) {
    if (!this.points.visible) return;
    this._time += dt;
    this.uniforms.uTime.value = this._time;
    // Ride with the camera so the sky never gets closer, and keep the sprites
    // comfortably inside the far plane where the depth test still has range.
    this.points.position.copy(this.camera.position);
  }

  dispose() {
    this.points.geometry.dispose();
    this.material.dispose();
  }
}

// ── Star colour ──────────────────────────────────────────────────────────────

/**
 * Build a 256-entry linear-RGB lookup indexed by the packed B−V byte.
 *
 * B−V is the star's blue-minus-visual colour index — negative is hot and blue,
 * positive is cool and red. Ballesteros' relation turns it into an effective
 * temperature, and the Planckian locus turns that into a colour.
 *
 * Each entry is normalised to unit luminance so the colour carries hue only and
 * magnitude alone sets brightness — otherwise blue stars would read brighter
 * than red ones of the same catalogue magnitude. `saturation` then pulls
 * everything toward white, because scotopic vision is nearly colourblind: real
 * naked-eye stars look far less saturated than their spectra suggest, and only
 * the brightest few show any tint at all.
 */
function buildColorLUT(saturation) {
  const lut = new Float32Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const bv = i / 40 - 0.5; // inverse of the packing in tools/build-stars.mjs

    // Ballesteros (2012). Fitted over roughly −0.4 ≤ B−V ≤ 2.0; the handful of
    // carbon stars beyond that just clamp to the red end.
    const b = Math.max(-0.4, Math.min(2.0, bv));
    const t = 4600 * (1 / (0.92 * b + 1.7) + 1 / (0.92 * b + 0.62));

    let [r, g, bl] = planckianSRGB(Math.max(1500, Math.min(40000, t)));

    // sRGB → linear, since everything upstream of OutputPass is linear.
    r = srgbToLinear(r); g = srgbToLinear(g); bl = srgbToLinear(bl);

    // Unit luminance, then desaturate toward white and re-normalise.
    let lum = 0.2126 * r + 0.7152 * g + 0.0722 * bl || 1;
    r /= lum; g /= lum; bl /= lum;

    r = 1 + (r - 1) * saturation;
    g = 1 + (g - 1) * saturation;
    bl = 1 + (bl - 1) * saturation;

    lum = 0.2126 * r + 0.7152 * g + 0.0722 * bl || 1;
    lut[i * 3] = r / lum;
    lut[i * 3 + 1] = g / lum;
    lut[i * 3 + 2] = bl / lum;
  }
  return lut;
}

/** Colour temperature → sRGB in 0..1 (Tanner Helland's Planckian fit). */
function planckianSRGB(kelvin) {
  const t = kelvin / 100;
  let r, g, b;

  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  }

  if (t >= 66) b = 255;
  else if (t <= 19) b = 0;
  else b = 138.5177312231 * Math.log(t - 10) - 305.0447927307;

  const c = v => Math.max(0, Math.min(255, v)) / 255;
  return [c(r), c(g), c(b)];
}

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
